import React, { useState, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import { ProductAddToCartWishlist } from '@src/stories/ecommerce/Product/ProductAddToCartWishlist/ProductAddToCartWishlist';

import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';
import { useCurrentTextStyle } from '@src/hooks/useCurrentTextStyle';

import api from 'frontend/js/api';

import { ILiquidProduct } from '@src/types/product';

import '@src/styles/pdp-sticky-bar-animations.css';
import { color } from 'framer-motion';

interface Variant {
  id: number;
  title: string;
  available: boolean;
  price?: number;
  option1?: string;
  option2?: string;
  option3?: string;
}

interface ColourOption {
  name: string;
  value: string;
  url: string;
  available: boolean;
}

interface SizeOption {
  id: number | string;
  title: string;
  subtext?: string;
  price?: number;
  available: boolean;
  additionalDetails?: string;
  productHandle?: string;
  url?: string;
  isTag?: boolean;
  productId?: string;
}

interface TagProduct {
  id: string;
  title: string;
  handle: string;
  tags: string[];
  availableForSale: boolean;
  variants: {
    edges: Array<{
      node: {
        id: string;
        availableForSale: boolean;
        price: {
          amount: string;
        };
      };
    }>;
  };
}

interface ComponentProps {
  settings?: any;
  product?: ILiquidProduct;
  product_form_id?: string;
  colour_swatches?: any;
  size_data?: any;
  tag_text?: string;
  [key: string]: any;
}

export const PDPStickyBar = (props: ComponentProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedColour, setSelectedColour] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<SizeOption | null>(null);
  const [showSelectionPanel, setShowSelectionPanel] = useState(false);
  const [panelType, setPanelType] = useState<'colour' | 'size' | 'combined' | null>(null);
  const [tagProducts, setTagProducts] = useState<TagProduct[]>([]);
  const [isLoadingTagProducts, setIsLoadingTagProducts] = useState(false);
  const stickyBarRef = useRef<HTMLDivElement>(null);

  // Destructure props
  const {
    settings = {},
    product,
    product_form_id: productFormId = '',
    colour_swatches = {},
    size_data = {},
    tag_text = ''
  } = props;

  const { isMobile, isTablet, isUltrawide } = useResponsiveBreakpoints();

  // ===================================================================
  // SETTINGS ORGANIZATION BY COMPONENT
  // Settings are grouped by their related component/functionality
  // with text style settings nested within each component group
  // ===================================================================

  // === STICKY BAR DISPLAY & POSITIONING SETTINGS ===
  const {
    trigger_offset = 550,
    trigger_offset_mobile = 300,
    trigger_offset_tablet = 300,
    trigger_offset_ultrawide = 550,
    always_show_desktop = false,
    always_show_mobile = false,
    always_show_tablet = false,
    always_show_ultrawide = false,
    background_color = '#ffffff',
    position_bottom = '16',
    position_left = '40',
    position_right = '40',
    position_bottom_mobile = '0',
    position_left_mobile = '0',
    position_right_mobile = '0',
    override_positioning_layout = true,
    position_layout_mobile_overrides = true
  } = settings;

  // === STICKY BAR LAYOUT & STYLING SETTINGS ===
  const {
    padding_y = 12,
    padding_x = 40,
    padding_y_mobile = 12,
    padding_x_mobile = 12,
    border_radius = 24,
    border_radius_mobile = 16,
    enable_border_radius_top_left = true,
    enable_border_radius_top_right = true,
    enable_border_radius_bottom_left = true,
    enable_border_radius_bottom_right = true,
    enable_border_radius_top_left_mobile = true,
    enable_border_radius_top_right_mobile = true,
    enable_border_radius_bottom_left_mobile = false,
    enable_border_radius_bottom_right_mobile = false,
    override_padding_layout = true,
    padding_layout_mobile_overrides = true,
    override_border_radius = true,
    override_border_radius_mobile = true
  } = settings;

  // === PRODUCT TAG TEXT SETTINGS ===
  const {
    tag_text_style = 'b1',
    tag_text_style_enable_responsive = false,
    tag_text_style_mobile = 'b1',
    tag_text_style_tablet = 'b1',
    tag_text_style_ultrawide = 'b1'
  } = settings;

  // === COLOUR OPTIONS SETTINGS ===
  const {
    swatch_size = 24,
    colour_title_text_style = 'b1',
    colour_title_text_style_enable_responsive = false,
    colour_title_text_style_mobile = 'b1',
    colour_title_text_style_tablet = 'b1',
    colour_title_text_style_ultrawide = 'b1'
  } = settings;

  // === SIZE OPTIONS SETTINGS ===
  const {
    size_option_show_border_selected = true,
    size_title_text_style = 'b1',
    size_title_text_style_enable_responsive = false,
    size_title_text_style_mobile = 'b1',
    size_title_text_style_tablet = 'b1',
    size_title_text_style_ultrawide = 'b1',
    size_button_text_style = 'b1',
    size_button_text_style_enable_responsive = false,
    size_button_text_style_mobile = 'b1',
    size_button_text_style_tablet = 'b1',
    size_button_text_style_ultrawide = 'b1'
  } = settings;

  // === ADD TO CART & WISHLIST BUTTON SETTINGS ===
  const {
    button_text_style = 'b1',
    button_text_style_enable_responsive = false,
    button_text_style_mobile = 'b1',
    button_text_style_tablet = 'b1',
    button_text_style_ultrawide = 'b1'
  } = settings;

  // === SIZE SELECTOR BUTTON TEXT SETTINGS ===
  const {
    size_selector_text_style = 'b1',
    size_selector_text_style_enable_responsive = false,
    size_selector_text_style_mobile = 'b1',
    size_selector_text_style_tablet = 'b1',
    size_selector_text_style_ultrawide = 'b1'
  } = settings;

  // === COLOUR SELECTOR BUTTON TEXT SETTINGS ===
  const {
    colour_selector_text_style = 'b1',
    colour_selector_text_style_enable_responsive = false,
    colour_selector_text_style_mobile = 'b1',
    colour_selector_text_style_tablet = 'b1',
    colour_selector_text_style_ultrawide = 'b1',
    colour_count_text_style = 'b1',
    colour_count_text_style_enable_responsive = false,
    colour_count_text_style_mobile = 'b1',
    colour_count_text_style_tablet = 'b1',
    colour_count_text_style_ultrawide = 'b1'
  } = settings;

  // === TEXT STYLE HOOKS ===
  // Apply responsive text styles for each component
  const { getCurrentTextStyle } = useCurrentTextStyle();

  const tagTextStyleClass = getCurrentTextStyle(
    tag_text_style,
    tag_text_style_enable_responsive,
    tag_text_style_mobile,
    tag_text_style_tablet,
    tag_text_style_ultrawide
  );

  const colourTitleTextStyleClass = getCurrentTextStyle(
    colour_title_text_style,
    colour_title_text_style_enable_responsive,
    colour_title_text_style_mobile,
    colour_title_text_style_tablet,
    colour_title_text_style_ultrawide
  );

  const sizeTitleTextStyleClass = getCurrentTextStyle(
    size_title_text_style,
    size_title_text_style_enable_responsive,
    size_title_text_style_mobile,
    size_title_text_style_tablet,
    size_title_text_style_ultrawide
  );

  const sizeButtonTextStyleClass = getCurrentTextStyle(
    size_button_text_style,
    size_button_text_style_enable_responsive,
    size_button_text_style_mobile,
    size_button_text_style_tablet,
    size_button_text_style_ultrawide
  );

  const sizeSelectorTextStyleClass = getCurrentTextStyle(
    size_selector_text_style,
    size_selector_text_style_enable_responsive,
    size_selector_text_style_mobile,
    size_selector_text_style_tablet,
    size_selector_text_style_ultrawide
  );

  const colourSelectorTextStyleClass = getCurrentTextStyle(
    colour_selector_text_style,
    colour_selector_text_style_enable_responsive,
    colour_selector_text_style_mobile,
    colour_selector_text_style_tablet,
    colour_selector_text_style_ultrawide
  );

  const colourCountTextStyleClass = getCurrentTextStyle(
    colour_count_text_style,
    colour_count_text_style_enable_responsive,
    colour_count_text_style_mobile,
    colour_count_text_style_tablet,
    colour_count_text_style_ultrawide
  );

  // Extract size settings from either main settings or size_data
  const sizeSettings = React.useMemo(() => {
    const {
      size_variant_source = 'variants',
      size_tag_name = '',
      size_field_to_display = 'title',
      size_tag_prefix_to_display = '',
      size_selection_action = 'redirect'
    } = size_data?.settings || settings;

    return {
      size_variant_source,
      size_tag_name,
      size_field_to_display,
      size_tag_prefix_to_display,
      size_selection_action
    };
  }, [settings, size_data]);

  const {
    size_variant_source,
    size_tag_name,
    size_field_to_display,
    size_tag_prefix_to_display,
    size_selection_action
  } = sizeSettings;

  // Function to fetch products by tag using GraphQL
  const fetchProductsByTag = async (tagFilter: string): Promise<TagProduct[]> => {
    if (!tagFilter) return [];

    setIsLoadingTagProducts(true);

    const endpoint = '/api/2024-01/graphql.json';
    // Get the access token from window or use a default
    const accessToken =
      (window as any)?.Shopify?.theme?.storefront_access_token ||
      (window as any)?.STOREFRONT_ACCESS_TOKEN ||
      api.getStorefrontToken() ||
      null;

    if (!accessToken) {
      console.error(
        'No Shopify Storefront access token found. Make sure the token is available in window.Shopify.theme.storefront_access_token'
      );
      setIsLoadingTagProducts(false);
      return [];
    }

    // Create search query - handle both exact tag matches and prefix matches
    const isExactTag = tagFilter.includes(':');

    if (!isExactTag) {
      setIsLoadingTagProducts(false);
      return [];
    }

    const searchQuery = `tag:"${tagFilter}"`;

    const query = `
      query GetProductsByTag(
        $query: String!
        $first: Int!
      ) {
        search(
          query: $query
          first: $first
          types: [PRODUCT]
        ) {
          edges {
            node {
              ... on Product {
                id
                title
                handle
                tags
                availableForSale
                variants(first: 5) {
                  edges {
                    node {
                      id
                      availableForSale
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      query: searchQuery,
      first: 10
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken
        },
        body: JSON.stringify({
          query: query,
          variables: variables
        })
      });

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        setIsLoadingTagProducts(false);
        return [];
      }

      const products = result?.data?.search?.edges?.map((edge: any) => edge.node) || [];

      // Filter products that actually contain the tag we're looking for
      const filteredProducts = products.filter((product: TagProduct) => {
        if (isExactTag) {
          // For exact tag matches, look for the exact tag value
          return product.tags.some((tag) => tag.toLowerCase() === tagFilter.toLowerCase());
        } else {
          // For prefix matches, look for tags that start with the prefix
          return product.tags.some((tag) => tag.toLowerCase().includes(tagFilter.toLowerCase()));
        }
      });

      setIsLoadingTagProducts(false);
      return filteredProducts;
    } catch (error) {
      console.error('Error fetching products by tag:', error);
      setIsLoadingTagProducts(false);
      return [];
    }
  };

  // Load tag products when size_variant_source is "tags" and size_tag_name is provided
  useEffect(() => {
    if (size_variant_source === 'tags' && size_tag_name && product?.tags) {
      // Find the actual tag value from the current product's tags
      const currentProductTag = product.tags.find((tag) => tag.toLowerCase().startsWith(size_tag_name.toLowerCase()));

      if (currentProductTag) {
        fetchProductsByTag(currentProductTag).then(setTagProducts);
      } else {
        // Fallback to the original size_tag_name if no matching tag found
        fetchProductsByTag(size_tag_name).then(setTagProducts);
      }
    }
  }, [size_variant_source, size_tag_name, product?.tags]);

  // Calculate positioning values
  const positionValues = React.useMemo(() => {
    const desktop = {
      bottom: `${position_bottom}px`,
      left: `${position_left}px`,
      right: `${position_right}px`,
      width: `calc(100% - ${position_left}px - ${position_right}px)`
    };

    const mobile =
      override_positioning_layout && position_layout_mobile_overrides
        ? {
            bottom: `${position_bottom_mobile}px`,
            left: `${position_left_mobile}px`,
            right: `${position_right_mobile}px`,
            width: `calc(100% - ${position_left_mobile}px - ${position_right_mobile}px)`
          }
        : desktop;

    return isMobile ? mobile : desktop;
  }, [
    isMobile,
    position_bottom,
    position_left,
    position_right,
    position_bottom_mobile,
    position_left_mobile,
    position_right_mobile,
    override_positioning_layout,
    position_layout_mobile_overrides
  ]);

  // Calculate padding values
  const paddingValues = React.useMemo(() => {
    const desktop = {
      paddingTop: `${padding_y}px`,
      paddingBottom: `${padding_y}px`,
      paddingLeft: `${padding_x}px`,
      paddingRight: `${padding_x}px`
    };

    const mobile =
      override_padding_layout && padding_layout_mobile_overrides
        ? {
            paddingTop: `${padding_y_mobile}px`,
            paddingBottom: `${padding_y_mobile}px`,
            paddingLeft: `${padding_x_mobile}px`,
            paddingRight: `${padding_x_mobile}px`
          }
        : desktop;

    return isMobile ? mobile : desktop;
  }, [
    isMobile,
    padding_y,
    padding_x,
    padding_y_mobile,
    padding_x_mobile,
    override_padding_layout,
    padding_layout_mobile_overrides
  ]);

  // Calculate border radius values
  const borderRadiusValues = React.useMemo(() => {
    const desktopRadius = border_radius;
    const mobileRadius = override_border_radius && override_border_radius_mobile ? border_radius_mobile : desktopRadius;

    const currentRadius = isMobile ? mobileRadius : desktopRadius;

    const desktop = {
      borderTopLeftRadius: enable_border_radius_top_left ? `${currentRadius}px` : '0px',
      borderTopRightRadius: enable_border_radius_top_right ? `${currentRadius}px` : '0px',
      borderBottomLeftRadius: enable_border_radius_bottom_left ? `${currentRadius}px` : '0px',
      borderBottomRightRadius: enable_border_radius_bottom_right ? `${currentRadius}px` : '0px'
    };

    if (isMobile && override_border_radius && override_border_radius_mobile) {
      return {
        borderTopLeftRadius: enable_border_radius_top_left_mobile ? `${mobileRadius}px` : '0px',
        borderTopRightRadius: enable_border_radius_top_right_mobile ? `${mobileRadius}px` : '0px',
        borderBottomLeftRadius: enable_border_radius_bottom_left_mobile ? `${mobileRadius}px` : '0px',
        borderBottomRightRadius: enable_border_radius_bottom_right_mobile ? `${mobileRadius}px` : '0px'
      };
    }

    return desktop;
  }, [
    isMobile,
    border_radius,
    border_radius_mobile,
    enable_border_radius_top_left,
    enable_border_radius_top_right,
    enable_border_radius_bottom_left,
    enable_border_radius_bottom_right,
    enable_border_radius_top_left_mobile,
    enable_border_radius_top_right_mobile,
    enable_border_radius_bottom_left_mobile,
    enable_border_radius_bottom_right_mobile,
    override_border_radius,
    override_border_radius_mobile
  ]);

  // Combined styles for the sticky bar
  const stickyBarStyles = React.useMemo(
    () => ({
      position: 'fixed' as const,
      zIndex: 1000,
      backgroundColor: background_color,
      boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
      ...positionValues,
      ...paddingValues,
      ...borderRadiusValues
    }),
    [background_color, positionValues, paddingValues, borderRadiusValues]
  );

  // Extract color options
  const colourOptions: ColourOption[] = React.useMemo(() => {
    const { swatches = [] } = colour_swatches;
    return swatches.map((swatch: any) => {
      const { name = '', colour, color, image, url = '', available = true } = swatch;
      return {
        name,
        value: colour || color || image || '',
        url,
        available: available !== false
      };
    });
  }, [colour_swatches]);

  // Extract size options from tag products, size_data, or fallback to product variants
  const sizeOptions: SizeOption[] = React.useMemo(() => {
    // Use tag-based products if we're using tags and have fetched products
    if (size_variant_source === 'tags') {
      // Check if the current product has the required tag prefix
      if (size_tag_prefix_to_display && product?.tags) {
        const hasRequiredTag = product.tags.some((tag) =>
          tag.toLowerCase().startsWith(`${size_tag_prefix_to_display.toLowerCase()}`)
        );

        if (!hasRequiredTag) {
          return [];
        }
      }

      if (tagProducts.length === 0) {
        // If no tag products found, fall back to size_data if available
        const { sizes = [] } = size_data;
        if (sizes.length > 1) {
          return sizes.map((size: any) => {
            const {
              id = '',
              title = '',
              available = true,
              price,
              additionalDetails = '',
              productHandle = '',
              url = ''
            } = size;
            return {
              id,
              title,
              available: available !== false,
              price,
              additionalDetails,
              productHandle,
              url
            };
          });
        }

        return [];
      }

      // Convert tag products to size options
      const tagOptions: SizeOption[] = tagProducts
        .map((tagProduct) => {
          // Extract the variant info for pricing/availability
          const firstVariant = tagProduct.variants?.edges?.[0]?.node;
          const isAvailable = tagProduct.availableForSale && (firstVariant?.availableForSale || false);

          // Create a clean title by finding and processing the matching tag
          let cleanTitle = tagProduct.title;
          let additionalDetails = '';

          if (size_field_to_display === 'first_variant_title') {
            cleanTitle = firstVariant?.title || '';
          } else if (size_field_to_display === 'tag') {
            const tags = tagProduct.tags || [];
            cleanTitle =
              tags
                .find((tag) => tag.toLowerCase().startsWith(`${size_tag_prefix_to_display.toLowerCase()}`))
                ?.replace(size_tag_prefix_to_display, '') || '';

            if (cleanTitle.includes('_')) {
              const parts = cleanTitle.split('_');
              cleanTitle = parts[0].trim();
              additionalDetails = parts[1]?.trim();
            }
          }

          if (!cleanTitle) return null;

          return {
            id: tagProduct.id,
            title: cleanTitle,
            price: null,
            available: isAvailable,
            isTag: true,
            productId: tagProduct.id,
            productHandle: tagProduct.handle,
            additionalDetails: additionalDetails || ''
          };
        })
        .filter(Boolean);

      // Sort by availability (available first) then by additional details or title
      return tagOptions.sort((a, b) => {
        const extractNumber = (val: string | undefined | null): number | null => {
          const match = val?.trim().match(/^(\d+(?:\.\d+)?)/);
          return match ? parseFloat(match[1]) : null;
        };

        const aVal = a.additionalDetails ?? '';
        const bVal = b.additionalDetails ?? '';

        const aNum = extractNumber(aVal);
        const bNum = extractNumber(bVal);

        if (aNum !== null && bNum !== null) {
          return aNum - bNum;
        } else if (aNum !== null) {
          return -1;
        } else if (bNum !== null) {
          return 1;
        } else {
          return aVal.localeCompare(bVal);
        }
      });
    }

    // Default to size_data if using variants source or no tag products
    const { sizes = [] } = size_data;
    if (sizes.length > 0) {
      return sizes.map((size: any) => {
        const {
          id = '',
          title = '',
          available = true,
          price,
          additionalDetails = '',
          productHandle = '',
          url = ''
        } = size;
        return {
          id,
          title,
          available: available !== false,
          price,
          additionalDetails,
          productHandle,
          url
        };
      });
    }

    // Fallback to product variants if no size_data
    const { variants = [] } = product || {};
    return variants.map((variant) => {
      const { id, title, available, price } = variant;
      return { id, title, available, price };
    });
  }, [
    size_variant_source,
    isLoadingTagProducts,
    tagProducts,
    size_data,
    product,
    size_field_to_display,
    size_tag_prefix_to_display
  ]);

  // Scroll detection
  useEffect(() => {
    let ticking = false;

    const checkVisibility = () => {
      const scrollY = window.scrollY;
      const currentTriggerOffset = isMobile
        ? trigger_offset_mobile
        : isTablet
        ? trigger_offset_tablet
        : isUltrawide
        ? trigger_offset_ultrawide
        : trigger_offset;
      const alwaysShow = isMobile
        ? always_show_mobile
        : isTablet
        ? always_show_tablet
        : isUltrawide
        ? always_show_ultrawide
        : always_show_desktop;
      const shouldShow = alwaysShow || scrollY > currentTriggerOffset;

      if (shouldShow !== isVisible) {
        setIsVisible(shouldShow);
      }

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(checkVisibility);
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    checkVisibility(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, [trigger_offset, trigger_offset_mobile, always_show_desktop, always_show_mobile, isMobile, isVisible]);

  // Handle selection panel interactions
  const handleColourClick = () => {
    if (isMobile) {
      if (showSelectionPanel && panelType === 'colour') {
        // Close panel if already open with colour panel
        setShowSelectionPanel(false);
        setPanelType(null);
      } else {
        // Open colour panel
        setPanelType('colour');
        setShowSelectionPanel(true);
      }
    } else {
      setPanelType('combined');
      setShowSelectionPanel(!showSelectionPanel);
    }
  };

  const handleSizeClick = () => {
    if (isMobile) {
      if (showSelectionPanel && panelType === 'size') {
        // Close panel if already open with size panel
        setShowSelectionPanel(false);
        setPanelType(null);
      } else {
        // Open size panel
        setPanelType('size');
        setShowSelectionPanel(true);
      }
    } else {
      setPanelType('combined');
      setShowSelectionPanel(!showSelectionPanel);
    }
  };

  const handleColourSelect = (colour: ColourOption) => {
    const { name, url } = colour;
    setSelectedColour(name);
    if (url) {
      window.location.href = url;
    }
    if (isMobile) {
      setShowSelectionPanel(false);
    }
  };

  const handleSizeSelect = (size: SizeOption) => {
    const { id } = size;
    setSelectedSize(size);

    // Handle tag-based selections
    if ('isTag' in size && size.isTag) {
      if (size_selection_action === 'redirect' && size.productHandle) {
        // Redirect to the product page
        window.location.href = `/products/${size.productHandle}`;
        return;
      } else if (size_selection_action === 'variant_change' && size.productId) {
        // Find the corresponding product in tagProducts and get its first variant
        const tagProduct = tagProducts.find((p) => p.id === size.productId);
        if (tagProduct?.variants?.edges?.[0]?.node?.id && productFormId) {
          const variantId = tagProduct.variants.edges[0].node.id;
          const variantInput = document.querySelector(`#${productFormId} input[name="id"]`) as HTMLInputElement;
          if (variantInput) {
            variantInput.value = variantId;
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        return;
      }
    }

    // Handle regular variant/size selections
    if (productFormId && id) {
      const variantInput = document.querySelector(`#${productFormId} input[name="id"]`) as HTMLInputElement;
      if (variantInput) {
        variantInput.value = id.toString();
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (isMobile) {
      setShowSelectionPanel(false);
    }
  };

  const handleClosePanel = () => {
    setShowSelectionPanel(false);
    setPanelType(null);
  };

  // Click outside to close panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (stickyBarRef.current && !stickyBarRef.current.contains(event.target as Node)) {
        handleClosePanel();
      }
    };

    if (showSelectionPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSelectionPanel]);

  // Set initial selections
  useEffect(() => {
    if (colourOptions.length > 0 && !selectedColour) {
      const currentColour = colourOptions.find(({ url }) => url.includes(window.location.pathname));
      if (currentColour) {
        setSelectedColour(currentColour.name);
      }
    }

    if (sizeOptions.length > 0 && !selectedSize) {
      // For tag-based sizes, find the option that matches the current product handle
      if (size_variant_source === 'tags' && product?.handle) {
        const currentProductOption = sizeOptions.find(
          (option) => 'productHandle' in option && option.productHandle === product.handle
        );

        if (currentProductOption) {
          setSelectedSize(currentProductOption);
          return;
        }
      }

      // Fallback: select first available option
      const firstAvailable = sizeOptions.find(({ available }) => available) || sizeOptions[0];
      setSelectedSize(firstAvailable);
    }
  }, [colourOptions, sizeOptions, selectedColour, selectedSize, size_variant_source, product?.handle]);

  const renderColourOptions = () => {
    if (colourOptions.length === 0) return null;

    return (
      <div className='flex items-center gap-4 lg:gap-6'>
        <h4 className={`${colourTitleTextStyleClass}`}>Colour</h4>
        <div className='flex flex-wrap gap-3'>
          {colourOptions.map((colour, index) => {
            const { name, value } = colour;
            const isSelected = selectedColour === name;
            const isImageValue = value.startsWith('http');

            return (
              <button
                key={index}
                onClick={() => handleColourSelect(colour)}
                className={`rounded-sm border transition-all ${
                  isSelected ? 'border-black' : 'border-transparent hover:border-gray-300'
                }`}
                style={{
                  width: `${swatch_size}px`,
                  height: `${swatch_size}px`,
                  backgroundColor: isImageValue ? 'transparent' : value,
                  backgroundImage: isImageValue ? `url(${value})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
                title={name}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const renderSizeOptions = () => {
    // Apply the same logic as the size selector button visibility
    if (sizeOptions.length === 0) return null;
    if (
      sizeOptions.length === 1 &&
      (sizeOptions[0].productHandle === product?.handle ||
        sizeOptions[0].id === product?.id ||
        sizeOptions[0].title === product?.title)
    ) {
      return null;
    }

    return (
      <div className='flex items-center gap-4 lg:gap-6'>
        <h4 className={`${sizeTitleTextStyleClass}`}>Size</h4>
        <div className='grid grid-cols-2 sm:flex flex-wrap gap-2 lg:gap-x-6 lg:gap-y-4'>
          {sizeOptions.map((size) => {
            const { id, title, available, additionalDetails } = size;
            const isSelected = selectedSize?.id === id;

            return (
              <button
                key={id}
                onClick={() => handleSizeSelect(size)}
                disabled={!available}
                className={`py-2 px-4 lg:p-3 rounded-xl transition-all ${sizeButtonTextStyleClass} ${
                  isSelected ? 'border border-[#C2C2C2]' : ''
                }`}
              >
                {title}
                {additionalDetails && <span className='ml-1 opacity-50'>{additionalDetails}</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSelectionPanel = () => {
    if (!showSelectionPanel) return null;

    // Check if size options should be shown
    const shouldShowSizeOptions =
      sizeOptions.length > 0 &&
      !(
        sizeOptions.length === 1 &&
        (sizeOptions[0].productHandle === product?.handle ||
          sizeOptions[0].id === product?.id ||
          sizeOptions[0].title === product?.title)
      );

    // Check if we should show the border between color and size sections
    const shouldShowBorder = panelType === 'combined' && colourOptions.length > 0 && shouldShowSizeOptions;

    return (
      <div className='lg:border-b border-[#EDEDED] pb-2 lg:pb-4 lg:mb-4'>
        {panelType === 'colour' || panelType === 'combined' ? renderColourOptions() : null}
        {shouldShowBorder && <div className='border-b border-[#EDEDED] my-4'></div>}
        {(panelType === 'size' || panelType === 'combined') && shouldShowSizeOptions ? renderSizeOptions() : null}
      </div>
    );
  };

  const optionsClass = sizeOptions.length > 0 && colourOptions.length > 0 ? 'lg:max-w-[364px]' : '';

  if (!product) return null;

  return (
    <CSSTransition
      in={isVisible}
      nodeRef={stickyBarRef}
      timeout={300}
      classNames='pdp-sticky'
      unmountOnExit
    >
      <div
        ref={stickyBarRef}
        style={stickyBarStyles}
      >
        <div className='relative'>
          {renderSelectionPanel()}

          <div className='flex items-center justify-between max-lg:gap-3 max-lg:pt-1 max-lg:pb-2'>
            {/* Product Info */}
            {tag_text && (
              <div className={`flex items-center gap-3 ${isMobile ? '' : 'min-w-[75px]'}`}>
                {/* Tag Text */}
                <p className={`${tagTextStyleClass}`}>{tag_text}</p>
              </div>
            )}

            {/* Controls */}
            <div className='flex items-center grow max-lg:gap-3'>
              {/* Size Selector Button */}
              {sizeOptions.length > 1 ||
              (sizeOptions.length === 1 &&
                !(
                  sizeOptions[0].productHandle === product?.handle ||
                  sizeOptions[0].id === product?.id ||
                  sizeOptions[0].title === product?.title
                )) ? (
                <div className={`basis-1/2 lg:basis-1/3 grow ${optionsClass}`}>
                  <button
                    onClick={handleSizeClick}
                    className={`flex justify-between items-center gap-2 lg:px-6 py-2 w-full ${
                      tag_text ? 'lg:pl-4 pl-2' : 'lg:pl-0 pl-0'
                    }`}
                  >
                    <span className={`${sizeSelectorTextStyleClass}`}>{selectedSize?.title || 'Size'}</span>
                    <i
                      className={`icon icon-chevron-down text-[20px] transition-transform duration-200 ${
                        showSelectionPanel && (panelType === 'size' || panelType === 'combined') ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                </div>
              ) : null}

              {sizeOptions.length > 0 && colourOptions.length > 0 && (
                <div className='max-lg:hidden h-7 w-px bg-[#C2C2C2]'></div>
              )}

              {/* Color Selector Button */}
              {colourOptions.length > 0 && (
                <div className={`basis-1/2 lg:basis-1/3 grow ${optionsClass}`}>
                  <button
                    onClick={handleColourClick}
                    className='flex justify-between items-center gap-2 lg:px-6 py-2 w-full'
                  >
                    <div className='flex items-center gap-2'>
                      {selectedColour && (
                        <div
                          className='w-4 h-4 rounded-sm border border-black'
                          style={{
                            backgroundColor: colourOptions.find(({ name }) => name === selectedColour)?.value
                          }}
                        />
                      )}
                      <span className={`${colourSelectorTextStyleClass}`}>{selectedColour || 'Colour'}</span>
                    </div>

                    <div className='flex items-center gap-2'>
                      {!isMobile && colourOptions.length > 1 && (
                        <span className={`text-black-50 ${isMobile ? 'hidden' : ''} ${colourCountTextStyleClass}`}>
                          +{colourOptions.length} Colours
                        </span>
                      )}
                      <i
                        className={`icon icon-chevron-down text-[20px] transition-transform duration-200 ${
                          showSelectionPanel && (panelType === 'colour' || panelType === 'combined') ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </button>
                </div>
              )}

              {sizeOptions.length === 0 && colourOptions.length === 0 && (
                <div className='pr-4 basis-1/3 grow b7--medium max-lg:hidden'>{product.title}</div>
              )}

              {/* Add to Cart + Wishlist */}
              <div className={`flex-shrink-0 basis-1/3 grow max-lg:hidden`}>
                <ProductAddToCartWishlist
                  product={product}
                  settings={{
                    button_text: settings.button_text || 'Add to Cart',
                    button_style: settings.button_style || 'secondary',
                    button_size: settings.button_size || 'lg',
                    button_classes: `${settings.button_classes || ''}`.trim(),
                    show_price: settings.show_price || true,
                    button_width: 100,
                    ...settings
                  }}
                />
              </div>
            </div>
          </div>
          <div className={`w-full lg:hidden`}>
            <ProductAddToCartWishlist
              product={product}
              settings={{
                button_text: settings.button_text || 'Add to Cart',
                button_style: settings.button_style || 'secondary',
                button_size: settings.button_size || 'lg',
                button_classes: `${settings.button_classes || ''}`.trim(),
                show_price: settings.show_price || true,
                button_width: 100,
                ...settings
              }}
            />
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};

export default PDPStickyBar;
